import { useEffect, useMemo, useState } from "react";

import { LoyaltyApi } from "@jti/rest-api";
import { type GetTiersResponseType } from "@jti/rest-api-types";
import { useSelector } from "@jti/store/reduxHooks";
import { Status } from "@jti/store/slices/loyaltyCoreSlice";
import { type RootStoreState } from "@jti/store/types";
import { type TiersNames } from "@jti/ui/components/TierPointsProgressBar";
import { type TierItemProps } from "@jti/ui/components/TiersStatuses";
import { useTranslations } from "@jti/ui/providers/useTranslations";
import { formatDate } from "@jti/ui/utils/formatDate/formatDate";

import { getTierDescription } from "./utils/getTierDescription";

type UseLoyaltyPointsStatusLogicResult = {
  isHidden: boolean;
  isLoading: boolean;
  userTierName: string;
  userTierToLowerCase: string;
  balance: number;
  pointsToExpire: number;
  expireAt: string;
  cumulativePoints: number;
  securedTillDate: string;
  progressBarPointsForGoldTier?: number;
  progressBarPointsForBlackTier?: number;
  tiersInfo: TierItemProps[];
  tiersNames: TiersNames;
  detailsBarRightTitle: string;
  currentTierAssignmentYear: string;
};

export const useLoyaltyPointsStatusLogic = (): UseLoyaltyPointsStatusLogicResult => {
  const [tiersThresholds, setTiersThresholds] = useState<GetTiersResponseType[]>([]);
  const [tiersAreLoading, setTiersAreLoading] = useState<boolean>(true);
  const isLogged = useSelector((state: RootStoreState) => state.loyaltyCore.isLogged);
  const isRestricted = useSelector((state: RootStoreState) => state.loyaltyCore.isRestricted);
  const {
    name: userTier,
    current_tier_secured_till,
    points_to_maintain_current_tier,
    current_tier_assignment_date,
    cumulative_points,
    points_to_next_tier,
    next_tier_name,
  } = useSelector((state: RootStoreState) => state.loyaltyCore.tier);

  const userTierName = userTier || "silver";

  const {
    balance,
    expire_at: expireAt,
    points_to_expire: pointsToExpire,
    status: pointsStatus,
  } = useSelector((state: RootStoreState) => state.loyaltyCore.points);
  const isLoading = tiersAreLoading || pointsStatus !== Status.COMPLETED;
  const { __ } = useTranslations();

  const userTierToLowerCase = userTierName.trim().toLowerCase();
  const isHidden = !isLogged || isRestricted;

  const currentTierThreshold =
    tiersThresholds.find((tier) => tier.name.toLowerCase() === userTierToLowerCase)?.condition_value || 0;

  const tiersInfo = useMemo<TierItemProps[]>(
    () =>
      tiersThresholds.map(({ name, condition_value: min }, index, arr) => {
        const max = index < arr.length - 1 ? `${arr[index + 1]?.condition_value - 1}` : `${min}+`;
        const pointsRangeLabel = index < arr.length - 1 ? `${min} - ${max}` : max;
        const currentTierIndex = arr.findIndex((tier) => tier.name.toLowerCase() === userTierToLowerCase);
        const isAchieved = index <= currentTierIndex;

        return {
          tierName: __("loyalty.pointsStatus.tierStatusTierNameLabel", [name]),
          pointsRange: __("loyalty.pointsStatus.tierStatusPointsRangeLabel", [pointsRangeLabel]),
          isAchieved,
        };
      }),
    [tiersThresholds, __, userTierToLowerCase]
  );

  const tiersNames: TiersNames = {
    silver: tiersThresholds[0]?.name,
    gold: tiersThresholds[1]?.name,
    black: tiersThresholds[2]?.name,
  };

  useEffect(() => {
    if (!isHidden) {
      LoyaltyApi.init()
        .getTiers()
        .then((response) => {
          setTiersThresholds(response.data);
          setTiersAreLoading(false);
        })
        .catch((error) => {
          console.error(error);
        });
    }
  }, [isHidden]);

  const detailsBarRightTitle = getTierDescription({
    isSecured: cumulative_points >= currentTierThreshold,
    currentTier: userTierName,
    pointsToNextTier: points_to_next_tier,
    nextTierName: next_tier_name,
    translateFunction: __,
    pointsToKeepTier: points_to_maintain_current_tier,
  });

  return {
    isHidden,
    isLoading,
    userTierName,
    userTierToLowerCase,
    balance,
    pointsToExpire,
    expireAt,
    cumulativePoints: cumulative_points,
    securedTillDate: formatDate(current_tier_secured_till),
    progressBarPointsForGoldTier: tiersThresholds[1]?.condition_value,
    progressBarPointsForBlackTier: tiersThresholds[2]?.condition_value,
    tiersInfo,
    tiersNames,
    detailsBarRightTitle,
    currentTierAssignmentYear: formatDate(current_tier_assignment_date, "YYYY"),
  };
};
